import {Icon} from "@iconify/react";
import {keepPreviousData, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {useState} from "react";
import PButton from "../../../components/PButton.tsx";
import TierEmblem from "../../../components/TierEmblem.tsx";
import coin from "../../../assets/earning-coins/coin.png";
import tier_bronze from "../../../assets/earning-coins/tier_bronze2.webp";
import tier_gold from "../../../assets/earning-coins/tier_gold2.webp";
import tier_platinum from "../../../assets/earning-coins/tier_platinum2.webp";
import tier_silver from "../../../assets/earning-coins/tier_silver2.webp";
import coinIcon from "../../../landing/assets/leaderboard/coin.svg";
import medalGold from "../../../landing/assets/leaderboard/medal_gold.webp";
import medalSilver from "../../../landing/assets/leaderboard/medal_silver.webp";
import medalBronze from "../../../landing/assets/leaderboard/medal_bronze.webp";
import {config} from "../../../config/config.ts";
import {UserRole} from "../../../constants.ts";
import {SPORT_AREA_LIST} from "../../../sports/sportAreas.ts";
import EarnAsYouPlayRedeemModal from "../../../components/modals/EarnAsYouPlayRedeemModal.tsx";
import {useGlobalModal} from "../../../core/GlobalModal.tsx";
import Header from "../../../Header.tsx";
import {useJwtUser} from "../../../JWTAuthProvider.tsx";
import services from "../../../services";
import {Tier} from "../../../types";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/earn/')({
    component: RouteComponent,
    // ?board=organisers opens the League of Organisers (sidebar link); default is League of
    // Leagues / League of Players.
    validateSearch: (search: Record<string, unknown>): {board?: 'organisers'} =>
        search.board === 'organisers' ? {board: 'organisers'} : {},
})

export function TierIcon({tier}: { tier: Tier }) {
    switch (tier) {
        case "none": return <></>
        case "bronze": return <img src={tier_bronze} alt={"Bronze"} />
        case "silver": return <img src={tier_silver} alt={"Silver"} />
        case "gold": return <img src={tier_gold} alt={"Gold"} />
        case "platinum": return <img src={tier_platinum} alt={"Platinum"} />
    }
}

function CoinsToTier(coins: number): Tier {
    if (coins >= 3000) return 'platinum';
    if (coins >= 1500) return 'gold';
    if (coins >= 500) return 'silver';
    if (coins >= 300) return 'bronze';
    return 'none';
}

function tierLabel(tier: string) {
    if (tier === 'none') return 'No Tier Yet';
    return tier.charAt(0).toUpperCase() + tier.slice(1) + ' Tier';
}

function CoinCard({value, title, subtitle, accent = 'amber'}: {value: number; title: string; subtitle: string; accent?: CardAccent}) {
    const a = CARD_ACCENTS[accent];
    return <div className={`relative overflow-hidden ${DARK_CARD} ${a.border} p-4 px-2 flex flex-col items-center justify-center text-center gap-2 transition-all duration-200 hover:-translate-y-0.5`}>
        <div className={`pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b ${a.glow} to-transparent`} />
        <div className="relative w-full max-w-16 aspect-square flex items-center justify-center">
            <span className="z-10 font-bold text-xl text-white pb-1">{value}</span>
            <img src={coin} alt="" className="absolute w-full h-full drop-shadow-[0_0_10px_rgba(255,196,46,0.35)]" />
        </div>
        <div className="relative">
            <div className="font-bold text-white text-sm">{title}</div>
            <div className={`text-xs ${a.text}`}>{subtitle}</div>
        </div>
    </div>
}

// Per-category accents — static class strings so Tailwind can see them.
const CARD_ACCENTS = {
    cyan:    {chip: 'bg-cyan-400/15 text-cyan-300',       text: 'text-cyan-300',    border: 'hover:border-cyan-400/60',    glow: 'from-cyan-400/15'},
    amber:   {chip: 'bg-amber-400/15 text-amber-300',     text: 'text-amber-300',   border: 'hover:border-amber-400/60',   glow: 'from-amber-400/15'},
    violet:  {chip: 'bg-violet-400/15 text-violet-300',   text: 'text-violet-300',  border: 'hover:border-violet-400/60',  glow: 'from-violet-400/15'},
    emerald: {chip: 'bg-emerald-400/15 text-emerald-300', text: 'text-emerald-300', border: 'hover:border-emerald-400/60', glow: 'from-emerald-400/15'},
    rose:    {chip: 'bg-rose-400/15 text-rose-300',       text: 'text-rose-300',    border: 'hover:border-rose-400/60',    glow: 'from-rose-400/15'},
    orange:  {chip: 'bg-orange-400/15 text-orange-300',   text: 'text-orange-300',  border: 'hover:border-orange-400/60',  glow: 'from-orange-400/15'},
    sky:     {chip: 'bg-sky-400/15 text-sky-300',         text: 'text-sky-300',     border: 'hover:border-sky-400/60',     glow: 'from-sky-400/15'},
} as const;
type CardAccent = keyof typeof CARD_ACCENTS;

// The shared dark-card look for this page (deep navy gradient, soft border).
const DARK_CARD = 'bg-gradient-to-b from-[#0E3450] to-[#0A2338] border border-white/10 rounded-2xl';

/**
 * One earning category: the headline number is COINS EARNED (not an event count — that read as
 * "410 games played"), with the real count spelled out underneath ("from 41 games").
 */
function CategoryCard({icon, label, value, count, countNoun, accent = 'cyan'}: {icon: string; label: string; value: number; count?: number; countNoun?: [string, string]; accent?: CardAccent}) {
    const a = CARD_ACCENTS[accent];
    return <div className={`relative overflow-hidden ${DARK_CARD} ${a.border} p-4 flex flex-col items-center text-center gap-2 transition-all duration-200 hover:-translate-y-0.5`}>
        {/* soft coloured glow bleeding from the top edge */}
        <div className={`pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b ${a.glow} to-transparent`} />
        <div className={`relative grid h-10 w-10 place-items-center rounded-xl ${a.chip}`}>
            <Icon icon={icon} className="text-xl" />
        </div>
        <div className="relative font-black text-white text-2xl flex items-center gap-1.5 leading-none">
            {value.toLocaleString()}
            <img src={coinIcon} alt="coins" className="w-4 h-4" />
        </div>
        <div className="relative text-xs text-white/60">
            {label}
            {count !== undefined && countNoun && (
                <span className="block text-white/35 mt-0.5">from {count} {count === 1 ? countNoun[0] : countNoun[1]}</span>
            )}
        </div>
    </div>
}

function TierCard({tier, title, subtitle}: {tier: Tier; title: string; subtitle: string}) {
    return <div className="bg-[#0d2847] border border-white/10 p-4 px-2 rounded-xl flex flex-col items-center justify-center text-center gap-2">
        <div className="relative max-w-20">
            <TierIcon tier={tier} />
        </div>
        <div className="font-bold text-white text-sm leading-tight">{title}</div>
        <div className="text-xs text-cyan-300">{subtitle}</div>
    </div>
}

function CountryFlag({code, size = 16}: { code?: string; size?: number }) {
    if (!code) return null;
    return <img className="inline" src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`} alt={code} style={{width: size, height: 'auto'}} />;
}

// Mini podium for embedding LOL/LOP
function EmbeddedPodiumCard({item, medal, borderColor, isFirst, isPlayer, rank}: {item: any; medal: string; borderColor: string; isFirst?: boolean; isPlayer?: boolean; rank: 1 | 2 | 3}) {
    // Podium stats flyout: hover on desktop, tap on touch. Pointer type keeps the two gestures
    // from fighting — a mouse hover never sticks, a tap toggles.
    const [statsOpen, setStatsOpen] = useState(false);
    const imgSize = isFirst ? 'w-20 h-20 sm:w-28 sm:h-28 lg:w-32 lg:h-32' : 'w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28';
    const cardWidth = isFirst ? 'w-24 sm:w-44 lg:w-52' : 'w-20 sm:w-40 lg:w-44';

    const glowGradient = rank === 1
        ? 'linear-gradient(179.66deg, rgba(254, 238, 172, 0) 0.29%, #FCDC5B 112.07%)'
        : rank === 2
        ? 'linear-gradient(179.66deg, rgba(217, 217, 217, 0) 0.29%, #9E9E9E 112.07%)'
        : 'linear-gradient(179.66deg, rgba(243, 202, 156, 0) 0.29%, #F3B56F 112.07%)';

    return (
        <div className={`relative flex flex-col items-center ${isFirst ? '' : 'mt-4 sm:mt-6 lg:mt-8'}${isPlayer ? ' cursor-pointer' : ''}`}
             onPointerEnter={(e) => isPlayer && e.pointerType === 'mouse' && setStatsOpen(true)}
             onPointerLeave={(e) => isPlayer && e.pointerType === 'mouse' && setStatsOpen(false)}
             onPointerUp={(e) => isPlayer && e.pointerType !== 'mouse' && setStatsOpen((o) => !o)}>
            <div
                className="absolute left-1/2 top-0 bottom-0 pointer-events-none -translate-x-1/2 w-[130%] sm:w-full"
                style={{
                    background: glowGradient,
                    filter: 'blur(25px)',
                    opacity: 0.75,
                    zIndex: 0,
                }}
            />
            <div className={`relative z-10 ${imgSize} rounded-xl overflow-hidden shadow-lg border-2 ${borderColor}`}>
                {(item.logo_url || item.profile_image_url || item.logo_thumb_url || item.profile_thumb_url) ? (
                    <img
                        src={item.logo_url || item.profile_image_url || item.logo_thumb_url || item.profile_thumb_url}
                        className="w-full h-full object-cover"
                        alt=""
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                            const t = e.currentTarget;
                            const fallback = item.logo_thumb_url || item.profile_thumb_url;
                            if (fallback && t.src !== fallback) {
                                t.src = fallback;
                            }
                        }}
                    />
                ) : (
                    <div className="w-full h-full bg-linear-to-br from-[#1a1f2e] to-[#0a0e1a] flex items-center justify-center">
                        <span className={`font-bebas text-[#6B8AFF] ${isFirst ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`}>{(item.title || item.name)?.charAt(0)}</span>
                    </div>
                )}
                <div className="absolute bottom-1 left-1 right-1 sm:bottom-2 sm:left-2 sm:right-2 bg-white/30 backdrop-blur-md rounded-md sm:rounded-lg px-1 sm:px-2 py-0.5 sm:py-1 border border-white/20 shadow-lg">
                    <p className="font-inter font-semibold text-white text-[8px] sm:text-[10px] truncate text-center drop-shadow">{item.title || item.name}</p>
                </div>
                {isPlayer && <TierEmblem tier={item.tier_rank || 'rookie'} className="absolute top-0.5 right-0.5 z-10 w-6 h-6 sm:w-8 sm:h-8" />}
            </div>
            <div className={`relative z-10 ${cardWidth} bg-[#1E2436] border border-white/10 rounded-2xl shadow-md text-center pt-4 sm:pt-6 pb-2 px-2 sm:px-6 mt-6 sm:mt-10`}>
                <img src={medal} alt="" className="w-6 h-6 sm:w-9 sm:h-9 absolute -top-3 sm:-top-5 left-1/2 -translate-x-1/2" />
                <p className="font-inter text-white/40 text-[9px] sm:text-xs truncate">{item.total_matches || item.games_played} {isPlayer ? 'games' : 'matches'}</p>
                <div className="flex items-center justify-center gap-1 sm:gap-1.5 mt-0.5">
                    <img src={coinIcon} alt="" className="w-3 h-3 sm:w-5 sm:h-5" />
                    <span className="font-inter font-black text-white text-sm sm:text-2xl lg:text-3xl">{(item.total_score || item.total_coins)?.toLocaleString()}</span>
                </div>
                <p className="font-inter text-white/40 text-[9px] sm:text-xs">{isPlayer ? 'Coins' : 'Points'}</p>
            </div>

            {/* Stats flyout — hover on desktop, tap on touch. Matches the table columns so the
                podium finally answers "on what?" without scrolling down to find them in the list. */}
            {isPlayer && statsOpen && (
                <div className="absolute left-1/2 top-full z-30 mt-2 w-44 -translate-x-1/2 rounded-xl border border-white/15 bg-[#0E1526] p-3 text-left shadow-2xl">
                    <div className="mb-2 flex items-center gap-1.5 border-b border-white/10 pb-2">
                        <span className="font-inter truncate text-xs font-bold text-white">{item.name}</span>
                        <CountryFlag code={item.country_code} size={14} />
                    </div>
                    <div className="grid grid-cols-2 gap-y-1.5 font-inter text-xs">
                        <span className="text-white/50">Rank</span><span className="text-right font-bold text-[#6B8AFF]">#{rank}</span>
                        <span className="text-white/50">Games</span><span className="text-right font-bold text-white">{item.games_played ?? 0}</span>
                        <span className="text-white/50">Wins</span><span className="text-right font-bold text-white">{item.match_wins ?? 0}</span>
                        <span className="text-white/50">Streaks</span><span className="text-right font-bold text-white">{item.streaks ?? 0}</span>
                        <span className="text-white/50">Coins</span>
                        <span className="flex items-center justify-end gap-1 font-bold text-[#F0C64A]">
                            <img src={coinIcon} alt="" className="h-3 w-3" />{item.total_coins?.toLocaleString()}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

type Scope = 'global' | 'country' | 'city';
type PeriodType = 'monthly' | 'yearly';

const SPORT_OPTIONS: { value: string; label: string }[] = [
    {value: '', label: 'All Sports'},
    {value: 'padel', label: 'Padel'},
    {value: 'tennis', label: 'Tennis'},
    {value: 'pickelball', label: 'Pickleball'},
    {value: 'squash', label: 'Squash'},
    {value: 'badminton', label: 'Badminton'},
];
// League of Leagues ranks team-sport leagues too (race areas have none). League of Players stays
// racquet-only: team-sport players aren't on it.
const TEAM_SPORT_OPTIONS = SPORT_AREA_LIST.filter((a) => !a.isRaceArea).map((a) => ({value: a.key, label: a.label}));

function getMonthOptions(): { value: string; label: string }[] {
    const now = new Date();
    const options: { value: string; label: string }[] = [];
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        options.push({
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleString('en', {month: 'long', year: 'numeric'}),
        });
    }
    return options;
}

function getYearOptions(): { value: string; label: string }[] {
    const now = new Date();
    const options: { value: string; label: string }[] = [];
    for (let i = 0; i < 5; i++) {
        const y = now.getFullYear() - i;
        options.push({value: String(y), label: String(y)});
    }
    return options;
}

function EnrollmentBanner({isOrganizer}: {isOrganizer: boolean}) {
    const user = useJwtUser();
    const [loading, setLoading] = useState(false);
    const isOptedOut = !!user?.leaderboard_opted_out_at;
    const isEnrolled = !!user?.leaderboard_enrolled_at;

    if (!isOptedOut && !isEnrolled) return null;

    const handleToggle = () => {
        setLoading(true);
        const action = isOptedOut
            ? services.leaderboardEnrollment.enroll()
            : services.leaderboardEnrollment.optOut();
        action.then(() => {
            window.location.reload();
        }).catch(() => setLoading(false));
    };

    return (
        <div className={`rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-3 ${isOptedOut ? 'bg-red-900/30 border border-red-500/30' : 'bg-green-900/30 border border-green-500/30'}`}>
            <div className="flex items-center gap-2">
                <Icon icon={isOptedOut ? 'material-symbols-light:visibility-off' : 'material-symbols-light:check-circle'} className={`text-xl ${isOptedOut ? 'text-red-400' : 'text-green-400'}`} />
                <span className="text-sm text-white">
                    {isOptedOut
                        ? `You have opted out of the ${isOrganizer ? 'League of Leagues' : 'League of Players'}`
                        : `You are enrolled in the ${isOrganizer ? 'League of Leagues' : 'League of Players'}`
                    }
                </span>
            </div>
            <PButton
                size="sm"
                color={isOptedOut ? 'success' : 'danger'}
                variant={isOptedOut ? 'solid' : 'flat'}
                isLoading={loading}
                onPress={handleToggle}
                className={isOptedOut ? 'text-white font-bold' : ''}
            >
                {isOptedOut ? 'Enroll' : 'Opt out'}
            </PButton>
        </div>
    );
}

function RouteComponent() {
    const user = useJwtUser();
    const userRole = user?.role_override || user?.role;
    const isOrganizer = userRole === UserRole.Organizer || userRole === UserRole.Master;
    // Two separate organiser boards, each with its own sidebar entry: League of Leagues (default)
    // and League of Organisers (?board=organisers).
    const {board} = Route.useSearch();
    // Flag-gated so a direct ?board=organisers URL falls back to League of Leagues where the
    // organizer gamification is dark (production).
    const orgBoard: 'leagues' | 'organisers' = board === 'organisers' && config.featureEnableOrganizerGamification ? 'organisers' : 'leagues';
    const globalModal = useGlobalModal();

    const [scope, setScope] = useState<Scope>('global');
    const [countryCode, setCountryCode] = useState('');
    const [city, setCity] = useState('');
    const [sportType, setSportType] = useState('');
    const [periodType, setPeriodType] = useState<PeriodType>('monthly');

    const monthOptions = getMonthOptions();
    const yearOptions = getYearOptions();
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
    const [selectedYear, setSelectedYear] = useState(yearOptions[0].value);

    const periodValue = periodType === 'monthly' ? selectedMonth : selectedYear;

    const statsQuery = useQuery({
        queryKey:        queryKeys.coins_stats(),
        queryFn:         services.coins.stats,
        enabled:         !!user?.uid,
        placeholderData: keepPreviousData,
    })

    const lolFilters = {
        ...(scope === 'country' && countryCode ? {country_code: countryCode} : {}),
        ...(scope === 'city' && countryCode ? {country_code: countryCode, city} : {}),
        ...(sportType ? {sport_type: sportType} : {}),
        period_type: periodType,
        period_value: periodValue,
    };
    const lopFilters = {
        per_page: 50,
        ...(scope === 'country' && countryCode ? {country_code: countryCode} : {}),
        ...(sportType ? {sport_type: sportType} : {}),
        period_type: periodType,
        period_value: periodValue,
    };

    // LOL data for organizers
    const lolQuery = useQuery({
        queryKey: queryKeys.league_of_leagues(lolFilters),
        queryFn: () => services.leagueOfLeagues.get(lolFilters),
        enabled: isOrganizer,
    });

    const allLolQuery = useQuery({
        queryKey: queryKeys.league_of_leagues({}),
        queryFn: () => services.leagueOfLeagues.get({}),
        enabled: isOrganizer,
    });

    // League of Organisers — organizers ranked by earned coins (people, not leagues); its own
    // page via ?board=organisers. No city/sport scoping in v1.
    const orgLbFilters = {
        per_page: 50,
        ...(scope !== 'global' && countryCode ? {country_code: countryCode} : {}),
        period_type: periodType,
        period_value: periodValue,
    };
    const orgLbQuery = useQuery({
        queryKey: queryKeys.organizer_leaderboard(orgLbFilters),
        queryFn: () => services.organizerLeaderboard.get(orgLbFilters),
        enabled: isOrganizer && orgBoard === 'organisers',
    });
    const allOrgLbQuery = useQuery({
        queryKey: queryKeys.organizer_leaderboard({per_page: 50}),
        queryFn: () => services.organizerLeaderboard.get({per_page: 50}),
        enabled: isOrganizer && orgBoard === 'organisers',
    });

    // LOP data for players
    const lopQuery = useQuery({
        queryKey: queryKeys.player_leaderboard(lopFilters),
        queryFn: () => services.playerLeaderboard.get(lopFilters),
        enabled: !isOrganizer,
    });

    const allLopQuery = useQuery({
        queryKey: queryKeys.player_leaderboard({per_page: 50}),
        queryFn: () => services.playerLeaderboard.get({per_page: 50}),
        enabled: !isOrganizer,
    });

    const leagueMode = isOrganizer && orgBoard === 'leagues';
    const organiserMode = isOrganizer && orgBoard === 'organisers';

    const allItems = leagueMode ? (allLolQuery.data?.leagues || [])
        : organiserMode ? (allOrgLbQuery.data?.organizers || [])
        : (allLopQuery.data?.players || []);
    const countries = [...new Set(allItems.map((i: any) => i.country_code).filter(Boolean))] as string[];
    const cities = leagueMode && countryCode
        ? [...new Set(allItems.filter((i: any) => i.country_code === countryCode).map((i: any) => i.city).filter(Boolean))] as string[]
        : [];

    const data = statsQuery.data;
    const myCoins = data?.myself_total_coins || 0;
    const tier = CoinsToTier(myCoins);
    const breakdown: any = data?.category_breakdown || {};

    const leaderboardItems = leagueMode ? (lolQuery.data?.leagues || [])
        : organiserMode ? (orgLbQuery.data?.organizers || [])
        : (lopQuery.data?.players || []);
    const hasPodium = leaderboardItems.length >= 3;
    const top3 = hasPodium ? leaderboardItems.slice(0, 3) : [];
    const rest = hasPodium ? leaderboardItems.slice(3, 13) : leaderboardItems.slice(0, 13);
    const myself = leagueMode ? lolQuery.data?.myself : organiserMode ? orgLbQuery.data?.myself : lopQuery.data?.myself;
    const myselfInTop = myself && rest.some((item: any) => item.uid === myself.uid);
    const showMyselfRow = myself && !myselfInTop && (myself.rank > 13 || !top3.some((item: any) => item.uid === myself.uid));

    const myLeagues = isOrganizer && user?.uid
        ? (allLolQuery.data?.leagues || []).filter((l: any) => l.user_uid === user.uid)
        : [];
    const orgStats = myLeagues.reduce((acc: any, l: any) => {
        acc.total_matches += l.total_matches || 0;
        acc.close_matches += l.close_matches || 0;
        acc.total_players += l.total_players || 0;
        acc.active_players += l.active_players || 0;
        return acc;
    }, {total_matches: 0, close_matches: 0, total_players: 0, active_players: 0});
    const notCloseMatches = orgStats.total_matches - orgStats.close_matches;
    const participationPct = orgStats.total_players > 0 ? Math.round((orgStats.active_players / orgStats.total_players) * 100) : 0;
    const activityAvg = orgStats.active_players > 0 ? (orgStats.total_matches / orgStats.active_players).toFixed(1) : '0';

    return <div className="bg-[#0a0e1a] min-h-screen">
        <Header />
            <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6">

                {/* Header */}
                <div className="flex max-sm:flex-col justify-between items-start mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-white mb-1">{organiserMode ? 'League of Organisers' : isOrganizer ? 'League of Leagues' : 'League of Players'}</h1>
                        <p className="text-sm text-white/50">{organiserMode
                            ? 'Earn coins for everything you do as an organiser and climb the rankings.'
                            : 'Collect coins for every action and climb the leaderboard.'}</p>
                    </div>
                    <PButton
                        color="primary"
                        startContent={<Icon icon="material-symbols-light:redeem" className="text-xl" />}
                        onPress={() => globalModal.open(<EarnAsYouPlayRedeemModal />)}
                    >
                        Redeem rewards
                    </PButton>
                </div>

                <EnrollmentBanner isOrganizer={isOrganizer} />

                {/* ===== PART 1: Embedded Leaderboard ===== */}
                <div className="bg-[#111827] border border-white/10 rounded-3xl p-3 sm:p-6 mb-6">
                    {/* Podium */}
                    {top3.length >= 3 && (
                        <div className="flex items-start justify-around gap-1 sm:gap-4 mb-6">
                            <EmbeddedPodiumCard item={top3[1]} medal={medalSilver} borderColor="border-blue-400/50" isPlayer={!leagueMode} rank={2} />
                            <EmbeddedPodiumCard item={top3[0]} medal={medalGold} borderColor="border-amber-400/50" isFirst isPlayer={!leagueMode} rank={1} />
                            <EmbeddedPodiumCard item={top3[2]} medal={medalBronze} borderColor="border-orange-400/50" isPlayer={!leagueMode} rank={3} />
                        </div>
                    )}

                    {/* Summary */}
                    <div className="flex justify-center mb-6">
                        <div className="bg-[#1a1f2e] border border-white/10 rounded-full px-6 py-2 inline-flex items-center gap-2">
                            <span className="font-inter text-xs text-white/50">Total</span>
                            <span className="font-inter text-xs font-bold text-white">{(organiserMode ? orgLbQuery.data?.total : lopQuery.data?.total || lolQuery.data?.leagues?.length || 0)?.toLocaleString() || 0}</span>
                            <span className="font-inter text-xs text-white/50">{organiserMode ? 'organisers' : leagueMode ? 'leagues' : 'players'} competing</span>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="border border-white/10 rounded-2xl overflow-hidden">
                        <div className="py-3 px-3 border-b border-white/10">
                            <h2 className="font-bebas text-xl text-white uppercase tracking-wide text-center mb-3">
                                {organiserMode ? 'League of Organisers' : isOrganizer ? 'League of Leagues' : 'League of Players'}
                            </h2>
                            <div className="flex flex-col items-center gap-2 mb-3">
                                    <div className="bg-[#1a1f2e] rounded-full p-1 inline-flex gap-1 border border-white/10">
                                        {(['monthly', 'yearly'] as PeriodType[]).map((p) => (
                                            <button
                                                key={p}
                                                onClick={() => setPeriodType(p)}
                                                className={`font-inter text-[10px] font-medium px-4 py-1 rounded-full transition ${
                                                    periodType === p ? 'bg-[#082F8A] text-white shadow-md' : 'text-white/60 hover:text-white'
                                                }`}
                                            >
                                                {p.charAt(0).toUpperCase() + p.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                    <select
                                        className="font-inter text-[10px] bg-[#1a1f2e] border border-white/10 text-white rounded-full px-3 py-1"
                                        value={periodType === 'monthly' ? selectedMonth : selectedYear}
                                        onChange={(e) => { if (periodType === 'monthly') setSelectedMonth(e.target.value); else setSelectedYear(e.target.value); }}
                                    >
                                        {(periodType === 'monthly' ? monthOptions : yearOptions).map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                            </div>
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                {((leagueMode ? ['global', 'country', 'city'] : ['global', 'country']) as Scope[]).map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => { setScope(s); if (s === 'global') { setCountryCode(''); setCity(''); } }}
                                        className={`font-inter text-[10px] font-medium px-3 py-1 rounded-full border transition ${
                                            scope === s ? 'bg-[#082F8A] text-white border-[#082F8A]' : 'text-white/50 border-white/20 hover:border-white/40'
                                        }`}
                                    >
                                        {s.charAt(0).toUpperCase() + s.slice(1)}
                                    </button>
                                ))}
                                {scope !== 'global' && (
                                    <select
                                        className="font-inter text-[10px] bg-[#1a1f2e] border border-white/20 text-white rounded-full px-2 py-1"
                                        value={countryCode}
                                        onChange={(e) => { setCountryCode(e.target.value); setCity(''); }}
                                    >
                                        <option value="">All Countries</option>
                                        {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                )}
                                {leagueMode && scope === 'city' && countryCode && (
                                    <select
                                        className="font-inter text-[10px] bg-[#1a1f2e] border border-white/20 text-white rounded-full px-2 py-1"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                    >
                                        <option value="">All Cities</option>
                                        {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                )}
                                {!organiserMode && <select
                                    className="font-inter text-[10px] bg-[#1a1f2e] border border-white/20 text-white rounded-full px-2 py-1"
                                    value={sportType}
                                    onChange={(e) => setSportType(e.target.value)}
                                >
                                    {(leagueMode ? [...SPORT_OPTIONS, ...TEAM_SPORT_OPTIONS] : SPORT_OPTIONS).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>}
                            </div>
                        </div>

                        {/* Header */}
                        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-inter font-semibold text-white/40 uppercase tracking-wider border-b border-white/10">
                            <div className="col-span-1">Rank</div>
                            <div className="col-span-4">{leagueMode ? 'League' : organiserMode ? 'Organiser' : 'Player'}</div>
                            {leagueMode ? <>
                                <div className="col-span-2 text-center">Comp</div>
                                <div className="col-span-2 text-center">Part</div>
                                <div className="col-span-2 text-center">Activity</div>
                            </> : organiserMode ? <>
                                <div className="col-span-2 text-center">Events</div>
                                <div className="col-span-2 text-center">Finished</div>
                                <div className="col-span-2 text-center">Awards</div>
                            </> : <>
                                <div className="col-span-2 text-center">Games</div>
                                <div className="col-span-2 text-center">Wins</div>
                                <div className="col-span-2 text-center">Streaks</div>
                            </>}
                            <div className="col-span-1 text-right">{leagueMode ? 'Points' : 'Coins'}</div>
                        </div>

                        {/* Rows */}
                        {rest.map((item: any, idx: number) => {
                            const isMe = user?.uid && item.uid === user.uid;
                            return (
                            <div key={item.uid} className={`flex md:grid md:grid-cols-12 gap-2 px-3 sm:px-4 py-2.5 items-center border-b border-white/5 text-sm ${isMe ? 'bg-[#082F8A]/20 border-l-2 border-l-[#6B8AFF]' : idx % 2 ? 'bg-white/2' : ''}`}>
                                <div className="md:col-span-1 shrink-0 w-6">
                                    <span className="font-inter font-bold text-[#6B8AFF]">{item.rank}</span>
                                </div>
                                <div className="md:col-span-4 flex items-center gap-2 flex-1 min-w-0">
                                    <div className="relative shrink-0">
                                        {(item.logo_thumb_url || item.profile_thumb_url || item.logo_url || item.profile_image_url) ? (
                                            <img src={item.logo_thumb_url || item.profile_thumb_url || item.logo_url || item.profile_image_url} className="w-7 h-7 rounded-lg object-cover" alt="" referrerPolicy="no-referrer" />
                                        ) : (
                                            <div className="w-7 h-7 rounded-lg bg-[#082F8A]/30 flex items-center justify-center">
                                                <span className="text-[#6B8AFF] text-xs font-bold">{(item.title || item.name)?.charAt(0)}</span>
                                            </div>
                                        )}
                                        {item.tier_rank !== undefined && <TierEmblem tier={item.tier_rank || 'rookie'} className="absolute -bottom-1 -right-1 w-4 h-4" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-inter font-medium text-white text-xs truncate">
                                            {item.title || item.name}
                                            {isMe && <span className="ml-1 text-[9px] bg-[#6B8AFF]/20 text-[#6B8AFF] px-1 py-0.5 rounded">YOU</span>}
                                        </div>
                                        <div className="font-inter text-[10px] text-white/30 flex items-center gap-1">
                                            <CountryFlag code={item.country_code} size={10} />
                                            <span className="truncate">{item.city || item.country_code || ''}</span>
                                            {isOrganizer && item.club_name && <>
                                                <span className="text-white/20">|</span>
                                                <span className="truncate">{item.club_name}</span>
                                            </>}
                                        </div>
                                    </div>
                                </div>
                                {leagueMode ? <>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.competitiveness_score}{periodType === 'monthly' && <span className="text-white/20">/300</span>}</div>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.participation_score}{periodType === 'monthly' && <span className="text-white/20">/400</span>}</div>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.activity_score}{periodType === 'monthly' && <span className="text-white/20">/400</span>}</div>
                                </> : organiserMode ? <>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.events_created}</div>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.events_finished}</div>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.awards}</div>
                                </> : <>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.games_played}</div>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.match_wins}</div>
                                    <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{item.streaks}</div>
                                </>}
                                <div className="md:col-span-1 text-right shrink-0">
                                    <span className="inline-flex items-center gap-1 bg-amber-500/20 px-2 py-0.5 rounded-full">
                                        <img src={coinIcon} alt="" className="w-3 h-3" />
                                        <span className="font-inter text-xs font-bold text-amber-400">{item.total_score || item.total_coins}</span>
                                    </span>
                                </div>
                            </div>
                        )
                        })}

                        {showMyselfRow && (
                            <>
                                <div className="flex items-center justify-center gap-2 py-1 border-b border-white/5 bg-[#0a0e1a]">
                                    <span className="text-white/20 text-xs">• • •</span>
                                </div>
                                <div className="flex md:grid md:grid-cols-12 gap-2 px-3 sm:px-4 py-2.5 items-center bg-[#082F8A]/20 border-l-2 border-l-[#6B8AFF] text-sm">
                                    <div className="md:col-span-1 shrink-0 w-6">
                                        <span className="font-inter font-bold text-[#6B8AFF]">{myself.rank}</span>
                                    </div>
                                    <div className="md:col-span-4 flex items-center gap-2 flex-1 min-w-0">
                                        {(myself.logo_thumb_url || myself.profile_thumb_url || myself.logo_url || myself.profile_image_url) ? (
                                            <img src={myself.logo_thumb_url || myself.profile_thumb_url || myself.logo_url || myself.profile_image_url} className="w-7 h-7 rounded-lg object-cover shrink-0" alt="" referrerPolicy="no-referrer" />
                                        ) : (
                                            <div className="w-7 h-7 rounded-lg bg-[#082F8A]/30 flex items-center justify-center shrink-0">
                                                <span className="text-[#6B8AFF] text-xs font-bold">{(myself.title || myself.name)?.charAt(0)}</span>
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="font-inter font-medium text-white text-xs truncate">
                                                {myself.title || myself.name}
                                                <span className="ml-2 text-[10px] bg-[#6B8AFF]/20 text-[#6B8AFF] px-1.5 py-0.5 rounded">YOU</span>
                                            </div>
                                            <div className="font-inter text-[10px] text-white/30 flex items-center gap-1">
                                                <CountryFlag code={myself.country_code} size={10} />
                                                <span className="truncate">{myself.city || myself.country_code || ''}</span>
                                                {isOrganizer && myself.club_name && <>
                                                    <span className="text-white/20">|</span>
                                                    <span className="truncate">{myself.club_name}</span>
                                                </>}
                                            </div>
                                        </div>
                                    </div>
                                    {leagueMode ? <>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.competitiveness_score}{periodType === 'monthly' && <span className="text-white/20">/300</span>}</div>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.participation_score}{periodType === 'monthly' && <span className="text-white/20">/400</span>}</div>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.activity_score}{periodType === 'monthly' && <span className="text-white/20">/400</span>}</div>
                                    </> : organiserMode ? <>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.events_created}</div>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.events_finished}</div>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.awards}</div>
                                    </> : <>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.games_played}</div>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.match_wins}</div>
                                        <div className="hidden md:block md:col-span-2 text-center text-xs text-white">{myself.streaks}</div>
                                    </>}
                                    <div className="md:col-span-1 text-right shrink-0">
                                        <span className="inline-flex items-center gap-1 bg-amber-500/20 px-2 py-0.5 rounded-full">
                                            <img src={coinIcon} alt="" className="w-3 h-3" />
                                            <span className="font-inter text-xs font-bold text-amber-400">{myself.total_score || myself.total_coins}</span>
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {organiserMode ? null : isOrganizer ? (
                    <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4 mb-6">
                        {/* Competitiveness Score */}
                        <div className="bg-[#0a4d6b] border border-cyan-500/20 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
                            <h3 className="text-sm font-bold text-white mb-3">Competitiveness Score</h3>
                            <div className="flex items-baseline gap-4 mb-2">
                                <div>
                                    <div className="text-3xl font-black text-green-400">{orgStats.close_matches}</div>
                                    <div className="text-[10px] text-white/50 uppercase">Close</div>
                                </div>
                                <div className="text-white/20">/</div>
                                <div>
                                    <div className="text-3xl font-black text-white/60">{notCloseMatches}</div>
                                    <div className="text-[10px] text-white/50 uppercase">Not Close</div>
                                </div>
                            </div>
                            <div className="text-xs text-white/50">{orgStats.total_matches} total matches</div>
                        </div>

                        <div className="bg-[#0a4d6b] border border-cyan-500/20 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
                            <h3 className="text-sm font-bold text-white mb-3">Participation Score</h3>
                            <div className="text-5xl font-black text-white mb-1">
                                {orgStats.active_players}<span className="text-white/30 text-2xl">/{orgStats.total_players}</span>
                            </div>
                            <div className="text-xs text-white/50">{participationPct}% active players</div>
                        </div>

                        <div className="bg-[#0a4d6b] border border-cyan-500/20 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
                            <h3 className="text-sm font-bold text-white mb-3">Activity Score</h3>
                            <div className="text-5xl font-black text-white mb-1">{activityAvg}</div>
                            <div className="text-xs text-white/50">matches per active player</div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4 mb-4">
                            {/* Points Scored Today — gold-forward hero number */}
                            <div className={`relative overflow-hidden ${DARK_CARD} p-5 flex flex-col items-center justify-center text-center`}>
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-amber-400/15 to-transparent" />
                                <h3 className="relative text-xs font-bold uppercase tracking-wider text-white/60 mb-3">Points Today</h3>
                                <div className="relative flex items-center gap-2.5">
                                    <img src={coinIcon} alt="coins" className="w-9 h-9 drop-shadow-[0_0_12px_rgba(255,196,46,0.45)]" />
                                    <span className="text-6xl font-black text-amber-300 leading-none">{data?.today_coins || 0}</span>
                                </div>
                                <div className="relative text-xs text-white/45 mt-2">coins earned today</div>
                            </div>

                            {/* Points Tier Badge — warm glow behind the medal */}
                            <div className={`relative overflow-hidden ${DARK_CARD} p-5 flex flex-col items-center justify-center text-center`}>
                                <div className="pointer-events-none absolute left-1/2 top-6 h-28 w-28 -translate-x-1/2 rounded-full bg-amber-400/20 blur-2xl" />
                                <h3 className="relative text-xs font-bold uppercase tracking-wider text-white/60 mb-3">Points Tier</h3>
                                <div className="relative max-w-20 mb-2">
                                    <TierIcon tier={tier} />
                                </div>
                                <div className="relative text-xl font-black text-white">{tierLabel(tier)}</div>
                                <div className="relative mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/60">
                                    <img src={coinIcon} alt="" className="w-3.5 h-3.5" />
                                    {myCoins.toLocaleString()} lifetime coins
                                </div>
                            </div>

                            {/* Recent Points Scored */}
                            <div className={`${DARK_CARD} p-5`}>
                                <h3 className="text-xs font-bold uppercase tracking-wider text-white/60 mb-3">Recent Points</h3>
                                {(data?.recent_points || []).length === 0 ? (
                                    <div className="text-sm text-white/40 text-center py-4">No points yet</div>
                                ) : (
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                                        {(data?.recent_points || []).map((entry: any, idx: number) => (
                                            <div key={idx} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-white/[.04] border border-white/[.06] hover:bg-white/[.08] transition-colors">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-md bg-emerald-400/15 text-emerald-300">+{entry.coins_change}</span>
                                                    <span className="text-xs text-white/90">{entry.label}</span>
                                                </div>
                                                <span className="text-[10px] text-white/30 whitespace-nowrap">{entry.created_at}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Category breakdown cards — coins earned per category, with the event count
                            underneath so a 410-coin total no longer reads as "410 games played". */}
                        <div className="grid grid-cols-4 max-sm:grid-cols-2 gap-3 mb-3">
                            <CategoryCard icon="material-symbols-light:sports-tennis" label="Games Played" value={breakdown.games_played || 0}
                                          count={breakdown.games_played_count ?? 0} countNoun={['game', 'games']} accent="cyan" />
                            <CategoryCard icon="material-symbols-light:trophy" label="Games Won" value={breakdown.games_won || 0}
                                          count={breakdown.games_won_count ?? 0} countNoun={['win', 'wins']} accent="amber" />
                            <CategoryCard icon="material-symbols-light:login" label="Daily Logins" value={breakdown.daily_logins || 0}
                                          count={breakdown.daily_logins_count ?? 0} countNoun={['login', 'logins']} accent="violet" />
                            <CategoryCard icon="material-symbols-light:trending-up" label="Beat Higher Ranked" value={breakdown.beat_higher || 0}
                                          count={breakdown.beat_higher_count ?? 0} countNoun={['upset win', 'upset wins']} accent="emerald" />
                        </div>
                        <div className="grid grid-cols-3 max-sm:grid-cols-2 gap-3 mb-6 sm:max-w-3/4 sm:mx-auto">
                            <CategoryCard icon="material-symbols-light:handshake" label="Match Making" value={breakdown.match_making || 0}
                                          count={breakdown.match_making_count ?? 0} countNoun={['match', 'matches']} accent="rose" />
                            <CategoryCard icon="material-symbols-light:local-fire-department" label="Weekly Streak" value={breakdown.weekly_streak || 0}
                                          count={breakdown.weekly_streak_count ?? 0} countNoun={['streak', 'streaks']} accent="orange" />
                            <CategoryCard icon="material-symbols-light:person-add" label="Invited Players" value={breakdown.invited_players || 0}
                                          count={breakdown.invited_players_count ?? 0} countNoun={['invite', 'invites']} accent="sky" />
                        </div>
                    </>
                )}

                {organiserMode && <div className="bg-gradient-to-b from-[#0B1F33] to-[#081826] border border-white/10 rounded-2xl p-5 mb-6">
                    <h2 className="text-lg font-bold text-white mb-1">Earning Coins</h2>
                    <p className="text-sm text-white/40 mb-4">Earn coins for everything you do as an organiser — redeem them for Event Passes and free months of Pro.</p>

                    <div className="grid grid-cols-4 max-md:grid-cols-2 gap-3 mb-3">
                        <CoinCard value={2} title="Daily Login" subtitle="+2 coins/day" accent="violet" />
                        <CoinCard value={10} title="Complete an Event" subtitle="1–8 entries" accent="cyan" />
                        <CoinCard value={25} title="Complete an Event" subtitle="9–32 entries" accent="amber" />
                        <CoinCard value={60} title="Complete an Event" subtitle="32+ entries" accent="orange" />
                    </div>
                    <div className="grid grid-cols-4 max-md:grid-cols-2 gap-3 mb-4">
                        <CoinCard value={5} title="Register a Player" subtitle="via invite" accent="sky" />
                        <CoinCard value={5} title="Register a Player" subtitle="via registration form" accent="emerald" />
                        <CoinCard value={50} title="Set Up Payments" subtitle="one time" accent="amber" />
                        <CoinCard value={25} title="Embed Event Code" subtitle="on your website" accent="rose" />
                    </div>

                    <div className="rounded-xl border border-white/[.06] bg-white/[.03] p-4">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-white/40 mb-2">Rules</div>
                        <ul className="text-xs text-white/45 md:columns-2 md:gap-8">
                            {[
                                'Completion coins apply to tournaments and leagues alike, tiered by entries.',
                                'Registration coins are awarded per player who joins via your invite or registration form.',
                                'Payments and embed coins are one-time awards.',
                                'All redemption requests are subject to verification upon submission.',
                            ].map((rule) => (
                                <li key={rule} className="mb-1.5 flex gap-2 break-inside-avoid">
                                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-sm bg-amber-400/70" />
                                    {rule}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>}

                {!isOrganizer && <>
                    <div className="bg-gradient-to-b from-[#0B1F33] to-[#081826] border border-white/10 rounded-2xl p-5 mb-6">
                        <h2 className="text-lg font-bold text-white mb-1">Earning Coins</h2>
                        <p className="text-sm text-white/40 mb-4">Earn coins by actively participating in ScorePal and inviting friends!</p>

                        {/* Accents mirror the category breakdown above so each earning type keeps
                            one colour across the whole page. */}
                        <div className="grid grid-cols-4 max-md:grid-cols-2 gap-3 mb-3">
                            <CoinCard value={10} title="Play a Match" subtitle="+10 coins" accent="cyan" />
                            <CoinCard value={10} title="Win a Match" subtitle="+10 bonus" accent="amber" />
                            <CoinCard value={15} title="Weekly Streak" subtitle="3+ games/week" accent="orange" />
                            <CoinCard value={50} title="Invite a Friend" subtitle="+50 coins" accent="sky" />
                        </div>
                        <div className="grid grid-cols-3 max-md:grid-cols-2 gap-3 mb-4 md:max-w-3/4 md:mx-auto">
                            <CoinCard value={25} title="Beat Higher Ranked" subtitle="SPPR based" accent="emerald" />
                            <CoinCard value={10} title="Match Making" subtitle="+10 extra" accent="rose" />
                            <CoinCard value={2} title="Daily Login" subtitle="+2 coins/day" accent="violet" />
                        </div>

                        <div className="rounded-xl border border-white/[.06] bg-white/[.03] p-4">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-white/40 mb-2">Rules</div>
                            <ul className="text-xs text-white/45 md:columns-2 md:gap-8">
                                {[
                                    'Only ranked games count, including League, Tournament, and One-Off Ranked matches.',
                                    'A streak is achieved by playing at least 3 matches within a single week.',
                                    'Beat a higher-ranked player (based on SPPR rating) to earn 25 bonus coins.',
                                    'Invite points are awarded once the invited player completes 2 or more games.',
                                    'Points are earned only for games played through the Matchmaking option.',
                                    'All redemption requests are subject to verification upon submission.',
                                    'Suspicious gameplay patterns will be monitored.',
                                ].map((rule) => (
                                    <li key={rule} className="mb-1.5 flex gap-2 break-inside-avoid">
                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-sm bg-amber-400/70" />
                                        {rule}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="bg-[#111827] border border-white/10 rounded-2xl p-5">
                        <h2 className="text-lg font-bold text-white mb-1">Coin Tiers</h2>
                        <p className="text-sm text-white/40 mb-4">Players collect coins and climb tiers:</p>

                        <div className="grid grid-cols-4 max-md:grid-cols-2 gap-3">
                            <TierCard tier="bronze" title="300-499 coins" subtitle="" />
                            <TierCard tier="silver" title="500-1499 coins" subtitle="" />
                            <TierCard tier="gold" title="1500-2999 coins" subtitle="" />
                            <TierCard tier="platinum" title="3000+ coins" subtitle="" />
                        </div>
                    </div>
                </>}

        </div>
    </div>
}
